
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { first, Observable, of } from 'rxjs';
import { BaseWizardComponent } from '../base-wizard.component';
import {
  AcknowledgementsDataModel,
  IAcknowledgementQuestion,
  IAcknowledgementQuestionModel,
  ILotPlanModel,
} from '../models/veg-wizard-models';
import { AcknowledgementService } from '../services/acknowledgement.service';
import { Logger } from '../services/logger-service';
import { UtilService } from '../services/util.service';
import { VegWizardDataService } from '../services/veg-wizard-data.service';

@UntilDestroy()
@Component({
  selector: 'app-acknowledgements',
  templateUrl: './acknowledgements.component.html',
  styleUrls: ['./acknowledgements.component.css'],
  imports: [ReactiveFormsModule],
})
export class AcknowledgementsComponent extends BaseWizardComponent {
  form: FormGroup;
  questionsRefData: IAcknowledgementQuestion[] = [];
  acknowledgementQuestionsDetails: IAcknowledgementQuestion = {};
  lotOnPlan: ILotPlanModel | undefined;

  koalaHabitatProtectionHtmlMessage = `Before clearing on the property you should contact the Department of the Environment, Tourism, Science and Innovation - Koala Team on: 13 QGOV (13 74 68) to ensure that clearing complies with the koala protection framework.`;
  stateCommercialTimberRightsHtmlMessage = `Before clearing vegetation on the property, you should contact the <a href="https://www.resources.qld.gov.au/" target="_blank">Department of Natural Resources and Mines, Manufacturing and Regional and Rural Development</a> on 13 74 68 as the State may have commercial timber rights.`;
  clearingInAForestAreaHtmlMessage = `If you are clearing in a forest consent area or a forest entitlement area, you must contact the <a href="https://www.resources.qld.gov.au/" target="_blank">Department of Natural Resources and Mines, Manufacturing and Regional and Rural Development</a> on 13 74 68.`;
  wetTropicsQldWorldHeritageAreaMessage =
    'This property is located in the Wet Tropics World Heritage Area. Before commencing clearing on the property you should contact the Wet Tropics Management Authority on phone (07) 4241 0500, wettropics@wtma.qld.gov.au or visit www.wettropics.gov.au';

  requiresRecordKeepingAcknowledgements = false;

  containsForestConsentArea = false;
  containsForestEntitlementArea = false;
  containsKoalaHabitatArea = false;
  containsKoalaPriorityArea = false;
  containsLocallyRefinedKoalaHabitatArea = false;
  containsSeqRegionalPlanArea = false;

  containsWetTropicsQldWorldHeritageArea = false;

  // validation props
  stateCommercialTimberRightsValid = false;
  koalaHabitatProtectionValid = false;
  recordKeepingAcknowledgementValid = false;
  isWetTropicsQldWorldHeritageAreaValid = false;

  showsRecordKeepingAcknowledgementQuestions = false;
  showStateCommercialTimberRights = false;
  showKoalaHabitatProtection = false;

  // Form getters
  get stateCommercialTimberRightsAccepted() {
    return this.form.get('stateCommercialTimberRightsAccepted');
  }
  get koalaHabitatProtectionAccepted() {
    return this.form.get('koalaHabitatProtectionAccepted');
  }
  get recordKeepingAcknowledgementAccepted() {
    return this.form.get('recordKeepingAcknowledgementAccepted');
  }
  get isWetTropicsQldWorldHeritageAreaAccepted() {
    return this.form.get('isWetTropicsQldWorldHeritageAreaAccepted');
  }

  constructor(
    private formBuilder: FormBuilder,
    private logger: Logger,
    private vwds: VegWizardDataService,
    private acknowledgementService: AcknowledgementService,
    private utilService: UtilService,
    router: Router
  ) {
    super();

    const categoryPurposes = this.vwds.getCategories();

    if (categoryPurposes !== null) {
      this.requiresRecordKeepingAcknowledgements = categoryPurposes.some(
        (category) => category.requiresAtLeastOneRecordKeepingAck === true
      );
      this.showsRecordKeepingAcknowledgementQuestions = categoryPurposes.some(
        (category) => category.showRecordKeepingAcknowledgements === true
      );
    }

    const acknowledgementsFromSession = this.vwds.getAcknowledgementsData();

    this.lotOnPlan = this.vwds.getLotPlanModel();

    if (this.lotOnPlan != null) {
      // Business wanted to remove ForestArea related acknowledgements - allow it to pass. see https://itp-qld.atlassian.net/browse/OSACN-50
      this.containsForestConsentArea = this.lotOnPlan.containsForestConsentArea;
      this.containsForestEntitlementArea =
        this.lotOnPlan.containsForestEntitlementArea;

      this.containsKoalaHabitatArea = this.lotOnPlan.containsKoalaHabitatArea;
      this.containsKoalaPriorityArea = this.lotOnPlan.containsKoalaPriorityArea;
      this.containsLocallyRefinedKoalaHabitatArea =
        this.lotOnPlan.containsLocallyRefinedKoalaHabitatArea;
      this.containsSeqRegionalPlanArea =
        this.lotOnPlan.containsSeqRegionalPlanArea;
      this.containsWetTropicsQldWorldHeritageArea =
        this.lotOnPlan.containsWetTropicsQldWorldHeritageArea;
    }

    // assign show or hide values
    // https://itp-qld.atlassian.net/browse/OSACN-134
    // DAF who administer commercial timber rights on non-freehold tenures have indicated that they no longer wish to have this acknowledgement on the notification form.
    this.showStateCommercialTimberRights = false;
    this.showKoalaHabitatProtection =
      this.containsKoalaHabitatArea ||
      this.containsKoalaPriorityArea ||
      this.containsLocallyRefinedKoalaHabitatArea;

    this.form = this.formBuilder.group({
      acknowledgements: [acknowledgementsFromSession?.acknowledgements],
      recordKeepingAcknowledgementAccepted: [
        acknowledgementsFromSession?.recordKeepingAcknowledgementAccepted,
        Validators.required,
      ],
      koalaHabitatProtectionMessage: [
        acknowledgementsFromSession?.koalaHabitatProtection?.message,
      ],
      koalaHabitatProtectionAccepted: [
        acknowledgementsFromSession?.koalaHabitatProtection?.response,
      ],
      stateCommercialTimberRightsMessage: [
        acknowledgementsFromSession?.stateCommercialTimberRights?.message,
      ],
      stateCommercialTimberRightsAccepted: [
        acknowledgementsFromSession?.stateCommercialTimberRights?.response,
      ],
      isWetTropicsQldWorldHeritageAreaAccepted: [
        acknowledgementsFromSession?.wetTropicsQldWorldHeritageArea?.response,
      ],
    });
  }

  ngOnInit(): void {
    this.getAcknowledgementQuestionsData$()
      .pipe(first(), untilDestroyed(this))
      .subscribe((questionResults: IAcknowledgementQuestion[]) => {
        this.questionsRefData = questionResults;

        this.performValidations(); // initial validations check for our controls
      });

    this.form.valueChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.performValidations();
    });
  }

  private performValidations() {
    // clear any form errors to start with
    this.recordKeepingAcknowledgementAccepted?.setErrors(null);
    this.stateCommercialTimberRightsAccepted?.setErrors(null);
    this.koalaHabitatProtectionAccepted?.setErrors(null);
    this.isWetTropicsQldWorldHeritageAreaAccepted?.setErrors(null);

    this.recordKeepingAcknowledgementValid =
      !this.requiresRecordKeepingAcknowledgements ||
      !this.showsRecordKeepingAcknowledgementQuestions ||
      (this.questionsRefData?.length > 0 &&
        this.recordKeepingAcknowledgementAccepted?.value === true);

    if (this.showsRecordKeepingAcknowledgementQuestions) {
      this.recordKeepingAcknowledgementValid =
        !!this.recordKeepingAcknowledgementAccepted?.value;

      if (!this.recordKeepingAcknowledgementValid) {
        this.recordKeepingAcknowledgementAccepted?.setErrors({
          Required: true,
        });
      }
    }

    if (this.showStateCommercialTimberRights) {
      this.stateCommercialTimberRightsValid =
        !!this.stateCommercialTimberRightsAccepted?.value;
      if (!this.stateCommercialTimberRightsValid) {
        this.form.controls['stateCommercialTimberRightsAccepted'].setErrors({
          Required: true,
        });
      }
    }

    if (this.showKoalaHabitatProtection) {
      this.koalaHabitatProtectionValid =
        !!this.koalaHabitatProtectionAccepted?.value;
      if (!this.koalaHabitatProtectionValid) {
        this.koalaHabitatProtectionAccepted?.setErrors({ Required: true });
      }
    }

    if (this.containsWetTropicsQldWorldHeritageArea) {
      this.isWetTropicsQldWorldHeritageAreaValid =
        !!this.isWetTropicsQldWorldHeritageAreaAccepted?.value;
      if (!this.isWetTropicsQldWorldHeritageAreaValid) {
        this.isWetTropicsQldWorldHeritageAreaAccepted?.setErrors({
          Required: true,
        });
      }
    }
  }

  getAcknowledgementQuestionsData$(): Observable<IAcknowledgementQuestion[]> {
    const categories = this.vwds.getCategories();

    const selectedCategoryPurposeId =
      categories.length > 0 ? categories[0].categoryPurposeId : undefined;
    if (!selectedCategoryPurposeId) return of([]);

    return this.acknowledgementService.getQuestions$(selectedCategoryPurposeId);
  }

  override next(): void {
    this.performValidations();
    this.form.markAsDirty();
    this.form.updateValueAndValidity();

    if (!this.form.valid) {
      this.logger.warning('Form is not valid, please check!');
      this.utilService.getFormValidationErrors(this.form);
      return;
    }

    this.setAcknowledgementsData$()
      .pipe(first())
      .subscribe((success) => {
        if (success) {
          super.next();
        }
      });
  }

  private setAcknowledgementsData$(): Observable<boolean> {
    const model = new AcknowledgementsDataModel();

    if (this.showStateCommercialTimberRights) {
      model.stateCommercialTimberRights.message =
        this.stateCommercialTimberRightsHtmlMessage; // get the proper html data
      model.stateCommercialTimberRights.response =
        this.stateCommercialTimberRightsAccepted?.value;
    }

    if (this.showKoalaHabitatProtection) {
      model.koalaHabitatProtection.message =
        this.koalaHabitatProtectionHtmlMessage;
      model.koalaHabitatProtection.response =
        this.koalaHabitatProtectionAccepted?.value;
    }

    if (this.containsWetTropicsQldWorldHeritageArea) {
      model.wetTropicsQldWorldHeritageArea.message =
        this.wetTropicsQldWorldHeritageAreaMessage;
      model.wetTropicsQldWorldHeritageArea.response =
        this.isWetTropicsQldWorldHeritageAreaAccepted?.value;
    }

    model.questions = this.questionsRefData.map((r) => {
      return {
        acknowledgementQuestionId: r.acknowledgementQuestionId,
        description: r.description,
      } as IAcknowledgementQuestionModel;
    });

    model.recordKeepingAcknowledgementAccepted =
      this.recordKeepingAcknowledgementAccepted?.value;

    return this.vwds.setAcknowledgement$(model);
  }

  stripTrailingPeriod(text: string | null | undefined) {
    if (!text) return undefined;
    return text.replace(/\.$/, '');
  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }
}
